import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Grid,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  PlayArrow as ProcessingIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  Share as ShareIcon,
  Delete as DeleteIcon,
  GridView as GridViewIcon,
  ViewList as ListIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  AutoAwesome as AutoAwesomeIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import {
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  InputAdornment,
  Pagination,
  Autocomplete,
  TableSortLabel
} from '@mui/material';
import axios from 'axios';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import ClientReviewExportModal from './ClientReviewExportModal';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Severity color mapping
const getSeverityColor = (severity) => {
  switch (severity) {
    case 'critical': return 'error';
    case 'high': return 'error';
    case 'moderate': return 'warning';
    case 'low': return 'info';
    case 'minimal': return 'success';
    case 'unknown': return 'warning';
    default: return 'default';
  }
};

// 'unknown' means the review could not judge risk, not that risk is low.
const getRiskLabel = (riskLevel) => {
  if (!riskLevel || riskLevel === 'unknown') return 'Not assessed (review manually)';
  return riskLevel;
};

// Status color mapping
const getStatusColor = (status) => {
  switch (status) {
    case 'completed': return 'success';
    case 'processing': return 'primary';
    case 'failed': return 'error';
    case 'cancelled': return 'default';
    case 'pending': return 'warning';
    case 'queued': return 'info';
    default: return 'default';
  }
};

// Status icon mapping
const getStatusIcon = (status) => {
  switch (status) {
    case 'completed': return <CheckIcon />;
    case 'processing': return <ProcessingIcon />;
    case 'failed': return <ErrorIcon />;
    case 'cancelled': return <CancelIcon />;
    case 'pending': return <PendingIcon />;
    case 'queued': return <PendingIcon />;
    default: return null;
  }
};

// Client review context (29 Sep 2026). Keys match the server's context config.
const DEFAULT_CONTEXT_CHOICES = {
  includeConversations: true,
  includeTriageData: true,
  includeOnboardingData: true,
  includeAssessments: true,
  includeGoals: true,
  includeCheckIns: true,
  includeJournalEntries: true,
  includeCareNotes: true,
  includeFormulation: true,
  includeReasoningTimeline: true,
  includeSpecialistConsultations: true,
  includeSafetyHistory: true,
  includeLiveCalls: true,
  includeMemory: true,
  includeProgramme: true,
  conversationLimit: 5,
  messageLimit: 100
};

const CONTEXT_SOURCE_OPTIONS = [
  { key: 'includeConversations', label: 'Therapy Conversations' },
  { key: 'includeTriageData', label: 'Triage Decision' },
  { key: 'includeOnboardingData', label: 'Onboarding' },
  { key: 'includeAssessments', label: 'Assessments (answers, battery, queued and declined)' },
  { key: 'includeGoals', label: 'Treatment Goals' },
  { key: 'includeCheckIns', label: 'Daily Check-ins' },
  { key: 'includeJournalEntries', label: 'Journal Entries' },
  { key: 'includeCareNotes', label: 'Care Notes' },
  { key: 'includeSafetyHistory', label: 'Alerts & Safety History' },
  { key: 'includeLiveCalls', label: 'Live Calls (notes, risk statement, QA)' },
  { key: 'includeMemory', label: 'Memory (episode summaries, remembered facts)' },
  { key: 'includeFormulation', label: 'Gen 2 Clinical Formulation' },
  { key: 'includeReasoningTimeline', label: 'Gen 2 Reasoning Timeline' },
  { key: 'includeSpecialistConsultations', label: 'Gen 2 Specialist Consultations' },
  { key: 'includeProgramme', label: 'Programme, Appointments & Consent' }
];

function ClientReviewsView({ token, userRole }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobResults, setJobResults] = useState([]);
  const [urgentFindings, setUrgentFindings] = useState([]);
  const [urgentFindingsByJob, setUrgentFindingsByJob] = useState({});

  // Create review form state
  const [availableClients, setAvailableClients] = useState([]);
  const [reviewType, setReviewType] = useState('safety_review');
  const [selectedClients, setSelectedClients] = useState([]);
  // What the review reads. Starts from the chosen review type and template
  // (their context settings), and the reviewer can change it before running.
  const [contextChoices, setContextChoices] = useState(DEFAULT_CONTEXT_CHOICES);
  const setContextChoice = (key, value) => setContextChoices(prev => ({ ...prev, [key]: value }));
  // NEW: Template system state
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [availableReviewTypes, setAvailableReviewTypes] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [customInstructions, setCustomInstructions] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Admin-only LLM override state
  const [showLlmOverride, setShowLlmOverride] = useState(false);
  const [llmProvider, setLlmProvider] = useState('');
  const [llmModel, setLlmModel] = useState('');
  const [llmTemperature, setLlmTemperature] = useState('');
  const [llmMaxTokens, setLlmMaxTokens] = useState('');
  const [parallelismOverride, setParallelismOverride] = useState('');
  const [availableLlmModels, setAvailableLlmModels] = useState([]);

  // NEW: Enhanced client selection state (for Create Review dialog)
  const [selectClientSearch, setSelectClientSearch] = useState('');
  const [debouncedSelectClientSearch, setDebouncedSelectClientSearch] = useState('');
  const [selectFilterTherapistId, setSelectFilterTherapistId] = useState('');
  const [selectFilterSupervisorId, setSelectFilterSupervisorId] = useState('');
  const [selectFilterServiceGroupId, setSelectFilterServiceGroupId] = useState('');
  const [selectClientPage, setSelectClientPage] = useState(1);
  const [selectClientTotal, setSelectClientTotal] = useState(0);
  const [selectClientTotalPages, setSelectClientTotalPages] = useState(0);
  const [selectFilterOptions, setSelectFilterOptions] = useState({
    therapists: [],
    clinicalSupervisors: [],
    serviceGroups: []
  });
  const [selectClientsLoading, setSelectClientsLoading] = useState(false);

  // View mode and display state - persisted in localStorage
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('clientReviewsViewMode');
    return saved || 'list'; // Default to list view
  });
  const [showFilters, setShowFilters] = useState(false);

  // Persist view mode changes to localStorage
  const handleViewModeChange = (newMode) => {
    if (newMode) {
      setViewMode(newMode);
      localStorage.setItem('clientReviewsViewMode', newMode);
    }
  };

  // Filter state - separate input values from applied filters for debouncing
  const [filterReviewType, setFilterReviewType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterUrgency, setFilterUrgency] = useState(''); // 'urgent', 'flagged', 'all'
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Search inputs (local state for typing) vs applied filters (triggers API call)
  const [clientSearchInput, setClientSearchInput] = useState('');
  const [requesterSearchInput, setRequesterSearchInput] = useState('');
  const [appliedClientSearch, setAppliedClientSearch] = useState('');
  const [appliedRequesterSearch, setAppliedRequesterSearch] = useState('');

  // Sorting state
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const pageSize = 20;

  // Summary generation state
  const [generateSummary, setGenerateSummary] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);

  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareJob, setShareJob] = useState(null);
  const [eligibleShareUsers, setEligibleShareUsers] = useState([]);
  const [currentShares, setCurrentShares] = useState([]);
  const [selectedShareUsers, setSelectedShareUsers] = useState([]);
  const [shareLoading, setShareLoading] = useState(false);

  // Export modal state
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportTarget, setExportTarget] = useState(null); // resultId or jobId
  const [exportScope, setExportScope] = useState('result'); // 'result' or 'job'

  // Check user roles for access control
  const roles = Array.isArray(userRole) ? userRole : [userRole];
  const isAdmin = roles.includes('admin');
  const isClinicalSupervisor = roles.includes('clinical_supervisor');
  const isTherapist = roles.includes('therapist') && !isAdmin && !isClinicalSupervisor;
  const hasPrivilegedAccess = isAdmin || isClinicalSupervisor;

  const loadJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Build query params with filters and sorting
      const params = new URLSearchParams();
      if (filterReviewType) params.append('reviewType', filterReviewType);
      if (filterStatus) params.append('status', filterStatus);
      if (filterUrgency) params.append('urgency', filterUrgency);
      if (appliedRequesterSearch) params.append('requesterSearch', appliedRequesterSearch);
      if (appliedClientSearch) params.append('clientSearch', appliedClientSearch);
      if (filterDateFrom) params.append('startDate', filterDateFrom);
      if (filterDateTo) params.append('endDate', filterDateTo);
      params.append('sortBy', sortBy);
      params.append('sortOrder', sortOrder);
      params.append('limit', pageSize);
      params.append('offset', (currentPage - 1) * pageSize);

      const response = await axios.get(`${API_BASE_URL}/client-reviews/history?${params.toString()}`, {
        headers: { 'x-auth-token': token }
      });
      setJobs(response.data.data.jobs || []);
      setTotalJobs(response.data.data.pagination?.total || response.data.data.jobs?.length || 0);
    } catch (err) {
      console.error('Error loading jobs:', err);
      setError(err.response?.data?.error || 'Failed to load review jobs');
    } finally {
      setLoading(false);
    }
  }, [token, filterReviewType, filterStatus, filterUrgency, appliedRequesterSearch, appliedClientSearch, filterDateFrom, filterDateTo, sortBy, sortOrder, currentPage, pageSize]);

  // Reload jobs when filters, sort, or page changes
  useEffect(() => {
    if (token) {
      loadJobs();
    }
  }, [token, loadJobs]);

  // Debounce search inputs - apply after 500ms of no typing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (clientSearchInput !== appliedClientSearch) {
        setAppliedClientSearch(clientSearchInput);
        setCurrentPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [clientSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (requesterSearchInput !== appliedRequesterSearch) {
        setAppliedRequesterSearch(requesterSearchInput);
        setCurrentPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [requesterSearchInput]);

  // Debounce search input for client selection in Create Review dialog
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSelectClientSearch(selectClientSearch);
      setSelectClientPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [selectClientSearch]);

  // Clear filters handler
  const handleClearFilters = () => {
    setFilterReviewType('');
    setFilterStatus('');
    setFilterUrgency('');
    setClientSearchInput('');
    setRequesterSearchInput('');
    setAppliedClientSearch('');
    setAppliedRequesterSearch('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setCurrentPage(1);
  };

  // Check if any filters are active
  const hasActiveFilters = filterReviewType || filterStatus || filterUrgency || clientSearchInput || requesterSearchInput || filterDateFrom || filterDateTo;

  // Share dialog handlers
  const handleOpenShareDialog = async (job) => {
    setShareJob(job);
    setShareDialogOpen(true);
    setShareLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/client-reviews/jobs/${job.jobId}/shares`,
        { headers: { 'x-auth-token': token } }
      );
      setEligibleShareUsers(response.data.data.eligibleUsers || []);
      setCurrentShares(response.data.data.currentShares || []);
      setSelectedShareUsers([]);
    } catch (err) {
      console.error('Error loading share data:', err);
      setError('Failed to load sharing options');
    } finally {
      setShareLoading(false);
    }
  };

  const handleShare = async () => {
    if (selectedShareUsers.length === 0) return;
    setShareLoading(true);
    try {
      // Send all user IDs in a single request
      const userIds = selectedShareUsers.map(user => user._id);
      await axios.post(
        `${API_BASE_URL}/client-reviews/jobs/${shareJob.jobId}/share`,
        { userIds },
        { headers: { 'x-auth-token': token } }
      );
      // Reload share data
      const response = await axios.get(
        `${API_BASE_URL}/client-reviews/jobs/${shareJob.jobId}/shares`,
        { headers: { 'x-auth-token': token } }
      );
      setCurrentShares(response.data.data.currentShares || []);
      setSelectedShareUsers([]);
      loadJobs(); // Refresh the job list to update share indicators
    } catch (err) {
      console.error('Error sharing review:', err);
      setError(err.response?.data?.error || 'Failed to share review');
    } finally {
      setShareLoading(false);
    }
  };

  const handleUnshare = async (userId) => {
    setShareLoading(true);
    try {
      await axios.delete(
        `${API_BASE_URL}/client-reviews/jobs/${shareJob.jobId}/share/${userId}`,
        { headers: { 'x-auth-token': token } }
      );
      // Reload share data
      const response = await axios.get(
        `${API_BASE_URL}/client-reviews/jobs/${shareJob.jobId}/shares`,
        { headers: { 'x-auth-token': token } }
      );
      setCurrentShares(response.data.data.currentShares || []);
      loadJobs(); // Refresh the job list
    } catch (err) {
      console.error('Error removing share:', err);
      setError(err.response?.data?.error || 'Failed to remove share');
    } finally {
      setShareLoading(false);
    }
  };

  const loadUrgentFindings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/client-reviews/requiring-action`, {
        headers: { 'x-auth-token': token }
      });
      const findings = response.data.data || [];
      setUrgentFindings(findings);

      // Group urgent findings by jobIdString for easy lookup
      const byJob = {};
      findings.forEach(finding => {
        const jobKey = finding.jobIdString; // Use the string jobId for matching
        if (jobKey) {
          if (!byJob[jobKey]) {
            byJob[jobKey] = [];
          }
          byJob[jobKey].push(finding);
        }
      });
      console.log('[ClientReviewsView] Urgent findings by job:', byJob);
      setUrgentFindingsByJob(byJob);
    } catch (err) {
      console.error('Error loading urgent findings:', err);
    }
  };

  // Load available clients using the new dedicated endpoint with filtering and pagination
  const loadAvailableClients = useCallback(async () => {
    try {
      setSelectClientsLoading(true);

      // Build query params
      const params = new URLSearchParams();
      if (debouncedSelectClientSearch) params.append('search', debouncedSelectClientSearch);
      if (selectFilterTherapistId) params.append('therapistId', selectFilterTherapistId);
      if (selectFilterSupervisorId) params.append('clinicalSupervisorId', selectFilterSupervisorId);
      if (selectFilterServiceGroupId) params.append('serviceGroupId', selectFilterServiceGroupId);
      params.append('page', selectClientPage);
      params.append('limit', 50);

      console.log('[ClientReviewsView] Loading clients with params:', params.toString());

      const response = await axios.get(
        `${API_BASE_URL}/client-reviews/available-clients?${params.toString()}`,
        { headers: { 'x-auth-token': token } }
      );

      const { clients, filters, pagination } = response.data.data;

      // Format clients for display
      const formattedClients = clients.map(client => ({
        _id: client._id,
        name: client.name || client.email,
        email: client.email,
        therapistName: client.assignedTherapist?.name || '-',
        therapistId: client.assignedTherapist?._id,
        supervisorName: client.clinicalSupervisor?.name || '-',
        supervisorId: client.clinicalSupervisor?._id,
        serviceGroupName: client.serviceGroups?.[0]?.groupId?.name || '-',
        serviceGroupId: client.serviceGroups?.[0]?.groupId?._id
      }));

      console.log('[ClientReviewsView] Loaded', formattedClients.length, 'clients out of', pagination.total);

      setAvailableClients(formattedClients);
      setSelectFilterOptions(filters);
      setSelectClientTotal(pagination.total);
      setSelectClientTotalPages(pagination.totalPages);
    } catch (err) {
      console.error('[ClientReviewsView] Error loading clients:', err);
      console.error('[ClientReviewsView] Error details:', err.response?.data);
    } finally {
      setSelectClientsLoading(false);
    }
  }, [token, debouncedSelectClientSearch, selectFilterTherapistId, selectFilterSupervisorId, selectFilterServiceGroupId, selectClientPage]);

  // Reload clients when filters or page changes (only when dialog is open)
  useEffect(() => {
    if (createDialogOpen && token) {
      loadAvailableClients();
    }
  }, [createDialogOpen, loadAvailableClients, token]);

  // Reset client selection filters when dialog opens
  const handleOpenCreateDialog = () => {
    setSelectClientSearch('');
    setDebouncedSelectClientSearch('');
    setSelectFilterTherapistId('');
    setSelectFilterSupervisorId('');
    setSelectFilterServiceGroupId('');
    setSelectClientPage(1);
    setSelectedClients([]);
    setCreateDialogOpen(true);
  };

  // Clear client selection filters
  const handleClearSelectClientFilters = () => {
    setSelectClientSearch('');
    setDebouncedSelectClientSearch('');
    setSelectFilterTherapistId('');
    setSelectFilterSupervisorId('');
    setSelectFilterServiceGroupId('');
    setSelectClientPage(1);
  };

  // Toggle client selection (persists across filter changes)
  const toggleClientSelection = (clientId) => {
    setSelectedClients(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  // Select all clients on current page
  const handleSelectAllOnPage = (event) => {
    if (event.target.checked) {
      const pageClientIds = availableClients.map(c => c._id);
      setSelectedClients(prev => {
        const existing = new Set(prev);
        pageClientIds.forEach(id => existing.add(id));
        return Array.from(existing);
      });
    } else {
      const pageClientIds = new Set(availableClients.map(c => c._id));
      setSelectedClients(prev => prev.filter(id => !pageClientIds.has(id)));
    }
  };

  // Check if all clients on current page are selected
  const allOnPageSelected = availableClients.length > 0 &&
    availableClients.every(c => selectedClients.includes(c._id));
  const someOnPageSelected = availableClients.some(c => selectedClients.includes(c._id)) && !allOnPageSelected;

  useEffect(() => {
    const type = availableReviewTypes.find(t => t.typeKey === reviewType);
    const typeConfig = type?.contextConfig || {};
    const override = selectedTemplate?.contextConfigOverride || {};
    const next = { ...DEFAULT_CONTEXT_CHOICES };
    for (const key of Object.keys(next)) {
      if (typeConfig[key] !== undefined && typeConfig[key] !== null) next[key] = typeConfig[key];
      if (override[key] !== undefined && override[key] !== null) next[key] = override[key];
    }
    setContextChoices(next);
  }, [reviewType, selectedTemplate, availableReviewTypes]);

  const loadReviewTypes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/review-types`, {
        headers: { 'x-auth-token': token }
      });
      const types = response.data.data || [];
      setAvailableReviewTypes(types);
      console.log('[ClientReviewsView] Loaded review types:', types.length);
    } catch (err) {
      console.error('[ClientReviewsView] Error loading review types:', err);
      // Fallback to legacy types if API not available
      setAvailableReviewTypes([
        { typeKey: 'safety_review', name: 'Safety Screening' },
        { typeKey: 'modality_fidelity', name: 'MBT Therapy Fidelity' },
        { typeKey: 'progress_assessment', name: 'Progress Assessment' },
        { typeKey: 'keyword_search', name: 'Keyword Search' }
      ]);
    }
  };

  const loadReviewTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/review-templates`, {
        headers: { 'x-auth-token': token }
      });
      setAvailableTemplates(response.data.templates || []);

      // Auto-select first template for current review type
      if (response.data.templates && response.data.templates.length > 0) {
        const matchingTemplate = response.data.templates.find(t => t.reviewType === reviewType);
        if (matchingTemplate) {
          setSelectedTemplateId(matchingTemplate._id);
          setSelectedTemplate(matchingTemplate);
        }
      }
    } catch (err) {
      console.error('[ClientReviewsView] Error loading templates:', err);
    }
  };

  const loadAvailableLlmModels = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/platform-settings/available-models`, {
        headers: { 'x-auth-token': token },
        params: { activeOnly: true }
      });
      setAvailableLlmModels(response.data.models || response.data || []);
    } catch (err) {
      console.error('[ClientReviewsView] Error loading LLM models:', err);
    }
  };

  useEffect(() => {
    if (token) {
      // loadJobs is handled by the filter/sort useEffect
      loadUrgentFindings();
      loadAvailableClients();
      loadReviewTypes();
      loadReviewTemplates();
      if (isAdmin) {
        loadAvailableLlmModels();
      }
    }
  }, [token, userRole]);

  const handleCreateReview = async () => {
    if (selectedClients.length === 0) {
      setError('Please select at least one client');
      return;
    }

    try {
      setError('');
      const systemPromptMap = {
        safety_review: 'safety_screening_v1',
        modality_fidelity: 'modality_fidelity_mbt_v1',
        keyword_search: 'keyword_search_v1',
        progress_assessment: 'progress_assessment_v1'
      };

      // Build request payload
      const payload = {
        reviewType,
        targetClients: selectedClients,
        reviewParameters: {
          systemPromptTemplate: systemPromptMap[reviewType], // Legacy fallback
          contextConfig: contextChoices
        }
      };

      // Include summary generation toggle
      if (generateSummary) {
        payload.generateSummary = true;
      }

      // NEW: Include template ID and custom instructions if selected
      if (selectedTemplateId) {
        payload.reviewTemplateId = selectedTemplateId;
      }
      if (customInstructions && customInstructions.trim() !== '') {
        payload.customInstructions = customInstructions.trim();
      }

      // Admin-only LLM override
      if (isAdmin && showLlmOverride && llmProvider) {
        payload.llmConfig = {
          provider: llmProvider,
          ...(llmModel ? { model: llmModel } : {}),
          ...(llmTemperature !== '' ? { temperature: parseFloat(llmTemperature) } : {}),
          ...(llmMaxTokens !== '' ? { maxTokens: parseInt(llmMaxTokens) } : {})
        };
      }
      if (isAdmin && parallelismOverride) {
        payload.parallelism = parseInt(parallelismOverride);
      }

      await axios.post(
        `${API_BASE_URL}/client-reviews/jobs`,
        payload,
        {
          headers: { 'x-auth-token': token }
        }
      );

      setCreateDialogOpen(false);
      setSelectedClients([]);
      setCustomInstructions('');
      setGenerateSummary(false);
      setShowLlmOverride(false);
      setLlmProvider('');
      setLlmModel('');
      setLlmTemperature('');
      setLlmMaxTokens('');
      setParallelismOverride('');
      loadJobs();
    } catch (err) {
      console.error('Error creating review:', err);
      setError(err.response?.data?.error || 'Failed to create review');
    }
  };

  const handleViewResults = async (job) => {
    try {
      // Fetch latest job data and results in parallel
      const [jobResponse, resultsResponse] = await Promise.all([
        axios.get(
          `${API_BASE_URL}/client-reviews/jobs/${job.jobId}`,
          { headers: { 'x-auth-token': token } }
        ),
        axios.get(
          `${API_BASE_URL}/client-reviews/jobs/${job.jobId}/results`,
          { headers: { 'x-auth-token': token } }
        )
      ]);
      setSelectedJob(jobResponse.data.data || job);
      setJobResults(resultsResponse.data.data.results || []);
      setResultsDialogOpen(true);
    } catch (err) {
      console.error('Error loading results:', err);
      setError(err.response?.data?.error || 'Failed to load results');
    }
  };

  const handleGenerateSummary = async (jobId) => {
    try {
      setGeneratingSummary(true);
      setError('');
      const response = await axios.post(
        `${API_BASE_URL}/client-reviews/jobs/${jobId}/generate-summary`,
        {},
        { headers: { 'x-auth-token': token } }
      );

      // Update the selected job with the new summary data
      if (selectedJob && selectedJob.jobId === jobId) {
        setSelectedJob(prev => ({
          ...prev,
          summaryStatus: response.data.data.summaryStatus,
          summaryGeneratedAt: response.data.data.summaryGeneratedAt,
          summaryResults: {
            ...prev.summaryResults,
            overallFindings: response.data.data.overallFindings,
            aggregateStatistics: response.data.data.aggregateStatistics
          },
          summaryTokensUsed: response.data.data.tokensUsed
        }));
      }

      // Refresh the jobs list
      loadJobs();
    } catch (err) {
      console.error('Error generating summary:', err);
      setError(err.response?.data?.error || 'Failed to generate summary');
    } finally {
      setGeneratingSummary(false);
    }
  };

  const handleCancelJob = async (jobId) => {
    try {
      await axios.post(
        `${API_BASE_URL}/client-reviews/jobs/${jobId}/cancel`,
        {},
        {
          headers: { 'x-auth-token': token }
        }
      );
      loadJobs();
    } catch (err) {
      console.error('Error cancelling job:', err);
      setError(err.response?.data?.error || 'Failed to cancel job');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // All filtering is now done server-side, jobs already contains filtered results
  const filteredJobs = jobs;

  // Calculate stats for summary chips - based on current page results
  const completedCount = filteredJobs.filter(j => j.status === 'completed').length;
  const processingCount = filteredJobs.filter(j => j.status === 'processing' || j.status === 'queued').length;
  const pendingCount = filteredJobs.filter(j => j.status === 'pending').length;

  // Calculate urgent count based on current page results
  const filteredUrgentCount = filteredJobs.reduce((count, job) => {
    const jobUrgentCount = urgentFindingsByJob[job.jobId]?.length || 0;
    return count + jobUrgentCount;
  }, 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Client Reviews</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          {/* View Toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, v) => handleViewModeChange(v)}
            size="small"
          >
            <ToggleButton value="grid">
              <Tooltip title="Grid View"><GridViewIcon /></Tooltip>
            </ToggleButton>
            <ToggleButton value="list">
              <Tooltip title="List View"><ListIcon /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Filter Toggle */}
          <Button
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            variant={showFilters ? 'contained' : 'outlined'}
            size="small"
          >
            Filters
          </Button>

          <Button
            startIcon={<RefreshIcon />}
            onClick={loadJobs}
            size="small"
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Create Review
          </Button>
        </Box>
      </Box>

      {/* Stats Summary */}
      <Box display="flex" gap={1} mb={2} flexWrap="wrap">
        <Chip label={`${completedCount} Completed`} color="success" size="small" variant="outlined" />
        <Chip label={`${processingCount} Processing`} color="primary" size="small" variant="outlined" />
        {filteredUrgentCount > 0 && <Chip label={`${filteredUrgentCount} Urgent`} color="error" size="small" />}
        <Chip label={`${pendingCount} Pending`} color="warning" size="small" variant="outlined" />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', alignSelf: 'center' }}>
          {totalJobs} total reviews
        </Typography>
      </Box>

      {/* Filter Panel */}
      <Collapse in={showFilters}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Review Type Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Review Type</InputLabel>
                <Select
                  value={filterReviewType}
                  onChange={(e) => { setFilterReviewType(e.target.value); setCurrentPage(1); }}
                  label="Review Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  {availableReviewTypes.map(t => (
                    <MenuItem key={t.typeKey} value={t.typeKey}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Status Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="processing">Processing</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="queued">Queued</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Urgency Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Urgency</InputLabel>
                <Select
                  value={filterUrgency}
                  onChange={(e) => { setFilterUrgency(e.target.value); setCurrentPage(1); }}
                  label="Urgency"
                >
                  <MenuItem value="">All Reviews</MenuItem>
                  <MenuItem value="urgent">
                    <Box display="flex" alignItems="center" gap={1}>
                      <WarningIcon fontSize="small" color="error" />
                      Urgent Only
                    </Box>
                  </MenuItem>
                  <MenuItem value="flagged">
                    <Box display="flex" alignItems="center" gap={1}>
                      <WarningIcon fontSize="small" color="warning" />
                      Flagged & Urgent
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Client Search - debounced */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Search Client"
                placeholder="Name or code..."
                value={clientSearchInput}
                onChange={(e) => setClientSearchInput(e.target.value)}
                helperText={clientSearchInput !== appliedClientSearch ? "Searching..." : ""}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                  endAdornment: clientSearchInput && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => { setClientSearchInput(''); setAppliedClientSearch(''); }}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            {/* Requester Search (Admin/Supervisor only) - debounced */}
            {hasPrivilegedAccess && (
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Requested By"
                  placeholder="Search name..."
                  value={requesterSearchInput}
                  onChange={(e) => setRequesterSearchInput(e.target.value)}
                  helperText={requesterSearchInput !== appliedRequesterSearch ? "Searching..." : ""}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                    endAdornment: requesterSearchInput && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => { setRequesterSearchInput(''); setAppliedRequesterSearch(''); }}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            )}

            {/* Sort Controls */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="createdAt">Date Created</MenuItem>
                  <MenuItem value="reviewType">Review Type</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} sm={3} md={1}>
              <Tooltip title={sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}>
                <IconButton onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}>
                  {sortOrder === 'desc' ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
                </IconButton>
              </Tooltip>
            </Grid>

            {/* Clear Filters */}
            <Grid item xs={6} sm={3} md={1}>
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Urgent Findings Alert - only shows for filtered results */}
      {filteredUrgentCount > 0 && (
        <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            {filteredUrgentCount} Finding{filteredUrgentCount > 1 ? 's' : ''} Requiring Immediate Action
          </Typography>
          <Typography variant="body2">
            Click on the reviews below to view critical findings.
          </Typography>
        </Alert>
      )}

      {/* Reviews Display */}
      {filteredJobs.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No reviews found. {hasActiveFilters ? 'Try adjusting your filters.' : 'Create your first client review to get started.'}
          </Typography>
        </Paper>
      ) : viewMode === 'list' ? (
        /* List View */
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Clients</TableCell>
                <TableCell>Requested By</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Findings</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredJobs.map((job) => {
                const jobUrgentCount = urgentFindingsByJob[job.jobId]?.length || 0;
                const hasUrgentFindings = jobUrgentCount > 0;
                const isSharedWithMe = job.isSharedWithMe;
                const isOwnReview = job.isOwnReview;

                return (
                  <TableRow
                    key={job.jobId}
                    hover
                    onClick={() => job.status === 'completed' && handleViewResults(job)}
                    sx={{
                      cursor: job.status === 'completed' ? 'pointer' : 'default',
                      ...(isSharedWithMe && {
                        bgcolor: 'rgba(33, 150, 243, 0.04)',
                        borderLeft: '4px solid',
                        borderLeftColor: 'info.main'
                      }),
                      ...(hasUrgentFindings && {
                        bgcolor: 'rgba(211, 47, 47, 0.08)'
                      })
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Chip
                          label={job.status}
                          color={getStatusColor(job.status)}
                          size="small"
                        />
                        {job.sharedWith?.length > 0 && (
                          <Tooltip title={`Shared with ${job.sharedWith.length} user(s)`}>
                            <ShareIcon fontSize="small" color="action" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={job.reviewType.replace(/_/g, ' ')}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {job.scopeType === 'single' ? '1' : job.targetClients?.length || 0}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {job.initiatedBy?.userName || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {job.initiatedBy?.userRole?.replace(/_/g, ' ')}
                        </Typography>
                        {isSharedWithMe && (
                          <Chip
                            label="Shared with you"
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(job.createdAt), 'MMM d, yyyy')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(job.createdAt), 'h:mm a')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {hasUrgentFindings ? (
                        <Chip
                          icon={<WarningIcon />}
                          label={`${jobUrgentCount} Urgent`}
                          color="error"
                          size="small"
                        />
                      ) : job.status === 'completed' && job.summaryResults?.flaggedClients?.length > 0 ? (
                        <Chip
                          label={`${job.summaryResults.flaggedClients.length} Flagged`}
                          color="warning"
                          size="small"
                        />
                      ) : job.status === 'processing' ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={16} />
                          <Typography variant="caption">
                            {job.progress?.percentComplete || 0}%
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} onClick={(e) => e.stopPropagation()}>
                        {job.status === 'completed' && (
                          <Tooltip title="View Results">
                            <IconButton
                              size="small"
                              color={hasUrgentFindings ? 'error' : 'primary'}
                              onClick={() => handleViewResults(job)}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPrivilegedAccess && job.status === 'completed' && isOwnReview && (
                          <Tooltip title="Share">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenShareDialog(job)}
                            >
                              <ShareIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(job.status === 'pending' || job.status === 'queued' || job.status === 'processing') && (
                          <Tooltip title="Cancel">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleCancelJob(job.jobId)}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        /* Grid View */
        <Grid container spacing={2}>
          {filteredJobs.map((job) => {
            const jobUrgentCount = urgentFindingsByJob[job.jobId]?.length || 0;
            const hasUrgentFindings = jobUrgentCount > 0;
            const isSharedWithMe = job.isSharedWithMe;
            const isOwnReview = job.isOwnReview;

            return (
              <Grid item xs={12} md={6} lg={4} key={job.jobId}>
                <Card
                  sx={{
                    ...(isSharedWithMe && {
                      borderLeft: '4px solid',
                      borderLeftColor: 'info.main',
                      bgcolor: 'rgba(33, 150, 243, 0.04)'
                    }),
                    ...(hasUrgentFindings && {
                      border: '2px solid',
                      borderColor: 'error.main',
                      boxShadow: '0 0 8px rgba(211, 47, 47, 0.3)'
                    })
                  }}
                >
                  <CardContent>
                    {/* Shared with you badge */}
                    {isSharedWithMe && (
                      <Chip
                        icon={<ShareIcon />}
                        label={`Shared by ${job.initiatedBy?.userName || 'Unknown'}`}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ mb: 1 }}
                      />
                    )}

                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={job.status}
                          color={getStatusColor(job.status)}
                          icon={getStatusIcon(job.status)}
                          size="small"
                        />
                        {hasUrgentFindings && (
                          <Chip
                            label={`${jobUrgentCount} Urgent`}
                            color="error"
                            icon={<WarningIcon />}
                            size="small"
                          />
                        )}
                        {job.sharedWith?.length > 0 && !isSharedWithMe && (
                          <Tooltip title={`Shared with ${job.sharedWith.length} user(s)`}>
                            <Chip
                              icon={<ShareIcon />}
                              label="Shared"
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                      </Box>
                      <Tooltip title={job.reviewType.replace(/_/g, ' ')}>
                        <Chip
                          label={job.reviewType.replace(/_/g, ' ')}
                          size="small"
                          variant="outlined"
                        />
                      </Tooltip>
                    </Box>

                    <Typography variant="h6" gutterBottom>
                      {job.scopeType === 'single' ? 'Single Client' : `${job.targetClients?.length || 0} Clients`}
                    </Typography>

                    {/* Requester info - always visible */}
                    <Typography variant="body2" color="text.secondary">
                      Requested by: <strong>{job.initiatedBy?.userName || 'Unknown'}</strong> ({job.initiatedBy?.userRole?.replace(/_/g, ' ') || 'Unknown'})
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {format(new Date(job.createdAt), 'MMM d, yyyy h:mm a')}
                    </Typography>

                    {job.status === 'processing' && job.progress && (
                      <Box mt={2}>
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption">
                            Progress
                          </Typography>
                          <Typography variant="caption">
                            {job.progress.processedClients || 0}/{job.progress.totalClients || 0}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={job.progress.percentComplete || 0}
                        />
                      </Box>
                    )}

                    {job.status === 'completed' && job.summaryResults?.flaggedClients?.length > 0 && (
                      <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 2 }}>
                        <Typography variant="caption">
                          {job.summaryResults.flaggedClients.length} client{job.summaryResults.flaggedClients.length > 1 ? 's' : ''} flagged for attention
                        </Typography>
                      </Alert>
                    )}
                  </CardContent>

                  <CardActions>
                    {job.status === 'completed' && (
                      <Button
                        size="small"
                        variant={hasUrgentFindings ? 'contained' : 'text'}
                        color={hasUrgentFindings ? 'error' : 'primary'}
                        startIcon={<ViewIcon />}
                        onClick={() => handleViewResults(job)}
                      >
                        {hasUrgentFindings ? 'View Urgent' : 'View Results'}
                      </Button>
                    )}
                    {hasPrivilegedAccess && job.status === 'completed' && isOwnReview && (
                      <Button
                        size="small"
                        startIcon={<ShareIcon />}
                        onClick={() => handleOpenShareDialog(job)}
                      >
                        Share
                      </Button>
                    )}
                    {(job.status === 'pending' || job.status === 'queued' || job.status === 'processing') && (
                      <Button
                        size="small"
                        startIcon={<CancelIcon />}
                        onClick={() => handleCancelJob(job.jobId)}
                        color="error"
                      >
                        Cancel
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Pagination */}
      {totalJobs > pageSize && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={Math.ceil(totalJobs / pageSize)}
            page={currentPage}
            onChange={(e, page) => setCurrentPage(page)}
            showFirstButton
            showLastButton
            color="primary"
          />
        </Box>
      )}

      {/* Create Review Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Client Review</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Review Type</InputLabel>
              <Select
                value={reviewType}
                onChange={(e) => {
                  setReviewType(e.target.value);
                  // Auto-select matching template when review type changes
                  const matchingTemplate = availableTemplates.find(t => t.reviewType === e.target.value);
                  if (matchingTemplate) {
                    setSelectedTemplateId(matchingTemplate._id);
                    setSelectedTemplate(matchingTemplate);
                  } else {
                    setSelectedTemplateId(null);
                    setSelectedTemplate(null);
                  }
                }}
                label="Review Type"
              >
                {availableReviewTypes.map(type => (
                  <MenuItem key={type.typeKey} value={type.typeKey}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* NEW: Template Selection */}
            {availableTemplates.length > 0 && (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Review Template</InputLabel>
                <Select
                  value={selectedTemplateId || ''}
                  onChange={(e) => {
                    setSelectedTemplateId(e.target.value);
                    const template = availableTemplates.find(t => t._id === e.target.value);
                    setSelectedTemplate(template || null);
                  }}
                  label="Review Template"
                >
                  {availableTemplates
                    .filter(t => t.reviewType === reviewType)
                    .map(template => (
                      <MenuItem key={template._id} value={template._id}>
                        {template.name} (v{template.version})
                      </MenuItem>
                    ))}
                </Select>
                {selectedTemplate && (
                  <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
                    {selectedTemplate.description}
                  </Typography>
                )}
              </FormControl>
            )}

            {/* Persona info when template has a linked reviewer persona */}
            {selectedTemplate?.reviewerPersonaId && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Uses <strong>{selectedTemplate.reviewerPersonaId.name || 'Therapy Reviewer Persona'}</strong> for dimension-based evaluation
                </Typography>
                {selectedTemplate.reviewerPersonaId.reviewConfiguration?.evaluationDimensions?.length > 0 && (
                  <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                    {selectedTemplate.reviewerPersonaId.reviewConfiguration.evaluationDimensions.map((dim, idx) => (
                      <Chip
                        key={idx}
                        label={dim.name?.replace(/_/g, ' ')}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
              </Alert>
            )}

            {/* NEW: Custom Instructions Field - Hidden for Therapists */}
            {!isTherapist && selectedTemplate && selectedTemplate.allowCustomInstructions && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Custom Instructions (Optional)"
                placeholder="e.g., 'Focus on recent mood changes' or 'Look for patterns in sleep quality'"
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
                helperText={`Add specific guidance for this review. ${500 - customInstructions.length} characters remaining.`}
                inputProps={{ maxLength: 500 }}
                sx={{ mb: 3 }}
              />
            )}

            <Typography variant="subtitle2" gutterBottom>
              Select Clients
              {selectedClients.length > 0 && (
                <Chip
                  label={`${selectedClients.length} selected`}
                  size="small"
                  color="primary"
                  onDelete={() => setSelectedClients([])}
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>

            {/* Search and Filters Row */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search name or email..."
                  value={selectClientSearch}
                  onChange={(e) => setSelectClientSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: selectClientSearch && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSelectClientSearch('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              {/* Therapist Filter - Hidden for Therapists (they only see their own clients) */}
              {!isTherapist && (
                <Grid item xs={6} sm={3} md={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Therapist</InputLabel>
                    <Select
                      value={selectFilterTherapistId}
                      onChange={(e) => { setSelectFilterTherapistId(e.target.value); setSelectClientPage(1); }}
                      label="Therapist"
                    >
                      <MenuItem value="">All Therapists</MenuItem>
                      {selectFilterOptions.therapists.map(t => (
                        <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {/* Supervisor Filter - Hidden for Therapists */}
              {!isTherapist && selectFilterOptions.clinicalSupervisors.length > 1 && (
                <Grid item xs={6} sm={3} md={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Supervisor</InputLabel>
                    <Select
                      value={selectFilterSupervisorId}
                      onChange={(e) => { setSelectFilterSupervisorId(e.target.value); setSelectClientPage(1); }}
                      label="Supervisor"
                    >
                      <MenuItem value="">All Supervisors</MenuItem>
                      {selectFilterOptions.clinicalSupervisors.map(s => (
                        <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {selectFilterOptions.serviceGroups.length > 0 && (
                <Grid item xs={6} sm={3} md={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Service Group</InputLabel>
                    <Select
                      value={selectFilterServiceGroupId}
                      onChange={(e) => { setSelectFilterServiceGroupId(e.target.value); setSelectClientPage(1); }}
                      label="Service Group"
                    >
                      <MenuItem value="">All Groups</MenuItem>
                      {selectFilterOptions.serviceGroups.map(g => (
                        <MenuItem key={g._id} value={g._id}>{g.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {(selectClientSearch || selectFilterTherapistId || selectFilterSupervisorId || selectFilterServiceGroupId) && (
                <Grid item xs={6} sm={3} md={2}>
                  <Button
                    size="small"
                    startIcon={<ClearIcon />}
                    onClick={handleClearSelectClientFilters}
                    sx={{ height: '100%' }}
                  >
                    Clear
                  </Button>
                </Grid>
              )}
            </Grid>

            {/* Client Table with Checkboxes */}
            <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto', mb: 2 }}>
              {selectClientsLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    Loading clients...
                  </Typography>
                </Box>
              ) : availableClients.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                  <Typography variant="body2" color="text.secondary">
                    No clients found matching your criteria
                  </Typography>
                </Box>
              ) : (
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allOnPageSelected}
                          indeterminate={someOnPageSelected}
                          onChange={handleSelectAllOnPage}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Therapist</TableCell>
                      <TableCell>Supervisor</TableCell>
                      <TableCell>Service Group</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {availableClients.map(client => (
                      <TableRow
                        key={client._id}
                        hover
                        onClick={() => toggleClientSelection(client._id)}
                        sx={{ cursor: 'pointer' }}
                        selected={selectedClients.includes(client._id)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedClients.includes(client._id)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{client.name}</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>{client.email}</TableCell>
                        <TableCell>{client.therapistName}</TableCell>
                        <TableCell>{client.supervisorName}</TableCell>
                        <TableCell>{client.serviceGroupName}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>

            {/* Pagination */}
            {selectClientTotalPages > 1 && (
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="caption" color="text.secondary">
                  Showing {availableClients.length} of {selectClientTotal} clients
                </Typography>
                <Pagination
                  count={selectClientTotalPages}
                  page={selectClientPage}
                  onChange={(e, page) => setSelectClientPage(page)}
                  size="small"
                  color="primary"
                />
              </Box>
            )}

            <Typography variant="subtitle2" gutterBottom>
              Include Context
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 2 }}>
                {CONTEXT_SOURCE_OPTIONS.map(({ key, label }) => (
                  <FormControlLabel
                    key={key}
                    control={<Checkbox checked={!!contextChoices[key]} onChange={(e) => setContextChoice(key, e.target.checked)} />}
                    label={label}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                <TextField
                  select
                  size="small"
                  label="Conversations"
                  value={contextChoices.conversationLimit}
                  onChange={(e) => setContextChoice('conversationLimit', e.target.value)}
                  sx={{ minWidth: 160 }}
                  disabled={!contextChoices.includeConversations}
                >
                  {[1, 3, 5, 10, 20].map(n => <MenuItem key={n} value={n}>{`Latest ${n}`}</MenuItem>)}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Messages per conversation"
                  value={contextChoices.messageLimit}
                  onChange={(e) => setContextChoice('messageLimit', e.target.value)}
                  sx={{ minWidth: 200 }}
                  disabled={!contextChoices.includeConversations}
                >
                  {[50, 100, 250, 500].map(n => <MenuItem key={n} value={n}>{`Last ${n}`}</MenuItem>)}
                </TextField>
              </Box>
              <Typography variant="caption" color="text.secondary">
                The review states how many messages it read out of the total, so a partial record is never presented as complete.
              </Typography>
            </Box>

            {/* Summary report toggle */}
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={generateSummary}
                    onChange={(e) => setGenerateSummary(e.target.checked)}
                  />
                }
                label="Generate summary report after reviews complete"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4.5 }}>
                An LLM will synthesize all individual reviews into a summary with key findings,
                priority actions, and recommendations.
              </Typography>
            </Box>
          </Box>

          {/* Admin-only: Advanced LLM Settings */}
          {isAdmin && (
            <Box sx={{ mt: 3 }}>
              <Button
                size="small"
                variant="text"
                onClick={() => setShowLlmOverride(!showLlmOverride)}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
              >
                {showLlmOverride ? '- Hide' : '+ Show'} Advanced: LLM Settings
              </Button>
              <Collapse in={showLlmOverride}>
                <Box sx={{ mt: 1, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                    Override the platform default LLM settings for this job only.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Provider</InputLabel>
                        <Select
                          value={llmProvider}
                          label="Provider"
                          onChange={(e) => { setLlmProvider(e.target.value); setLlmModel(''); }}
                        >
                          <MenuItem value="">Platform Default</MenuItem>
                          <MenuItem value="vertex_ai">Vertex AI</MenuItem>
                          <MenuItem value="gemini">Gemini</MenuItem>
                          <MenuItem value="openai">OpenAI</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small" disabled={!llmProvider}>
                        <InputLabel>Model</InputLabel>
                        <Select
                          value={llmModel}
                          label="Model"
                          onChange={(e) => setLlmModel(e.target.value)}
                        >
                          <MenuItem value="">Provider Default</MenuItem>
                          {availableLlmModels
                            .filter(m => m.provider === llmProvider && m.isActive && !m.isDeprecated)
                            .map(m => (
                              <MenuItem key={m.id} value={m.id}>
                                {m.displayName} ({m.contextWindow?.toLocaleString()} tokens)
                              </MenuItem>
                            ))
                          }
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Temperature"
                        type="number"
                        value={llmTemperature}
                        onChange={(e) => setLlmTemperature(e.target.value)}
                        inputProps={{ min: 0, max: 2, step: 0.1 }}
                        disabled={!llmProvider}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Max Tokens"
                        type="number"
                        value={llmMaxTokens}
                        onChange={(e) => setLlmMaxTokens(e.target.value)}
                        inputProps={{ min: 100, max: 32000, step: 256 }}
                        disabled={!llmProvider}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Parallelism"
                        type="number"
                        value={parallelismOverride}
                        onChange={(e) => setParallelismOverride(e.target.value)}
                        inputProps={{ min: 1, max: 10 }}
                        helperText="1-10"
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateReview}
            disabled={selectedClients.length === 0}
          >
            Create Review
          </Button>
        </DialogActions>
      </Dialog>

      {/* Results Dialog */}
      <Dialog
        open={resultsDialogOpen}
        onClose={() => setResultsDialogOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          Review Results
          {selectedJob && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Job: {selectedJob.jobId} | Type: {selectedJob.reviewType.replace(/_/g, ' ')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Requested by: {selectedJob.initiatedBy?.userName || 'Unknown'} ({selectedJob.initiatedBy?.userRole || 'Unknown'}) on {format(new Date(selectedJob.createdAt), 'MMM d, yyyy h:mm a')}
              </Typography>
              {selectedJob.customInstructions && (
                <Box mt={1} p={1} bgcolor="#fff3e0" borderRadius={1}>
                  <Typography variant="caption" fontWeight="bold" color="text.primary">
                    Custom Instructions:
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    {selectedJob.customInstructions}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {/* Summary Report Section */}
          {selectedJob && selectedJob.status === 'completed' && (
            <Box sx={{ mb: 3 }}>
              {/* Generate Summary button - shown when no summary or failed */}
              {(!selectedJob.summaryStatus || selectedJob.summaryStatus === 'none' || selectedJob.summaryStatus === 'failed') && (
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Button
                    variant="outlined"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={() => handleGenerateSummary(selectedJob.jobId)}
                    disabled={generatingSummary}
                  >
                    {generatingSummary ? 'Generating...' : 'Generate Summary Report'}
                  </Button>
                  {selectedJob.summaryStatus === 'failed' && (
                    <Typography variant="caption" color="error">
                      Previous attempt failed: {selectedJob.summaryError || 'Unknown error'}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Generating indicator */}
              {(selectedJob.summaryStatus === 'generating' || generatingSummary) && (
                <Box display="flex" alignItems="center" gap={2} mb={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Generating summary report...
                  </Typography>
                </Box>
              )}

              {/* Summary display panel */}
              {selectedJob.summaryStatus === 'completed' && selectedJob.summaryResults?.overallFindings && (
                <Paper variant="outlined" sx={{ p: 3, mb: 2, bgcolor: '#f8f9ff', borderColor: '#c5cae9' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6">Summary Report</Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="caption" color="text.secondary">
                        Generated {selectedJob.summaryGeneratedAt ? format(new Date(selectedJob.summaryGeneratedAt), 'MMM d, yyyy h:mm a') : ''}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<AutoAwesomeIcon />}
                        onClick={() => handleGenerateSummary(selectedJob.jobId)}
                        disabled={generatingSummary}
                      >
                        Regenerate
                      </Button>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  {/* Aggregate statistics chips */}
                  {selectedJob.summaryResults?.aggregateStatistics && (
                    <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                      <Chip
                        label={`${selectedJob.summaryResults.aggregateStatistics.totalClientsReviewed || 0} clients reviewed`}
                        size="small"
                        variant="outlined"
                      />
                      {selectedJob.summaryResults.aggregateStatistics.clientsRequiringAction > 0 && (
                        <Chip
                          label={`${selectedJob.summaryResults.aggregateStatistics.clientsRequiringAction} requiring action`}
                          size="small"
                          color="error"
                        />
                      )}
                      {selectedJob.summaryResults.aggregateStatistics.clientsWithSafetyFlags > 0 && (
                        <Chip
                          label={`${selectedJob.summaryResults.aggregateStatistics.clientsWithSafetyFlags} with safety flags`}
                          size="small"
                          color="warning"
                        />
                      )}
                      {selectedJob.summaryResults.aggregateStatistics.riskDistribution && (
                        <>
                          {(selectedJob.summaryResults.aggregateStatistics.riskDistribution.critical || 0) > 0 && (
                            <Chip label={`${selectedJob.summaryResults.aggregateStatistics.riskDistribution.critical} critical`} size="small" color="error" />
                          )}
                          {(selectedJob.summaryResults.aggregateStatistics.riskDistribution.high || 0) > 0 && (
                            <Chip label={`${selectedJob.summaryResults.aggregateStatistics.riskDistribution.high} high risk`} size="small" color="error" variant="outlined" />
                          )}
                        </>
                      )}
                    </Box>
                  )}

                  {/* Rendered summary text */}
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'inherit',
                      lineHeight: 1.7,
                      '& h2, & ##': { fontSize: '1.1rem', fontWeight: 'bold', mt: 2, mb: 1 },
                      '& h3': { fontSize: '1rem', fontWeight: 'bold', mt: 1.5, mb: 0.5 },
                      '& ul': { pl: 2 },
                      '& li': { mb: 0.5 }
                    }}
                  >
                    {selectedJob.summaryResults.overallFindings}
                  </Typography>
                </Paper>
              )}
            </Box>
          )}

          {jobResults.map((result) => (
            <Box key={result._id} sx={{ mb: 4, p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
              {/* Client Header */}
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box>
                  <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <Typography variant="h6">
                      {result.clientName || result.clientCode}
                    </Typography>
                    <Chip
                      label={getRiskLabel(result.analysis?.overallAssessment?.riskLevel)}
                      color={getSeverityColor(result.analysis?.overallAssessment?.riskLevel)}
                      size="small"
                    />
                    {result.requiresImmediateReview && (
                      <Chip label="Requires Action" color="error" size="small" icon={<WarningIcon />} />
                    )}
                  </Box>
                  <Box display="flex" flexWrap="wrap" gap={2} sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                    {result.clientEmail && (
                      <Typography variant="body2" color="text.secondary">
                        {result.clientEmail}
                      </Typography>
                    )}
                    {result.therapistName && (
                      <Typography variant="body2" color="text.secondary">
                        Therapist: <strong>{result.therapistName}</strong>
                      </Typography>
                    )}
                    {result.supervisorNames && result.supervisorNames.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Supervisor{result.supervisorNames.length > 1 ? 's' : ''}: <strong>{result.supervisorNames.join(', ')}</strong>
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.7 }}>
                      ID: {result.clientCode}
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" gap={1}>
                  {/* View Client button - only for Clinical Supervisors, not Admin */}
                  {isClinicalSupervisor && !isAdmin && result.therapistId && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const clientId = result.clientId?._id || result.clientId;
                        navigate(`/clinical-supervisor/therapists/${result.therapistId}/clients/${clientId}`);
                      }}
                    >
                      View Client
                    </Button>
                  )}
                  <Tooltip title="Export this result">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setExportTarget(result._id);
                        setExportScope('result');
                        setExportModalOpen(true);
                      }}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Narrative Report (Primary - always displayed if present) */}
              {result.analysis?.narrative && (
                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Clinical Analysis Report
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: '#fafafa',
                      '& h2': { fontSize: '1.1rem', fontWeight: 'bold', mt: 2, mb: 1 },
                      '& h3': { fontSize: '1rem', fontWeight: 'bold', mt: 1.5, mb: 0.5 },
                      '& ul': { pl: 2 },
                      '& li': { mb: 0.5 }
                    }}
                  >
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'inherit',
                        lineHeight: 1.6
                      }}
                    >
                      {result.analysis.narrative}
                    </Typography>
                  </Paper>
                  {result.analysis?.structuredExtractionSucceeded === false && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Note: Structured data extraction was not available for this review.
                    </Typography>
                  )}
                </Box>
              )}

              {/* Dimension Evaluation (shown when persona review data exists) */}
              {result.analysis?.personaReview && (
                <Box mb={3}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Dimension Evaluation — {result.analysis.personaReview.personaName}
                    </Typography>
                    <Chip
                      label={`${Math.round(result.analysis.personaReview.weightedQualityScore)}%`}
                      color={
                        result.analysis.personaReview.weightedQualityScore >= 70 ? 'success' :
                        result.analysis.personaReview.weightedQualityScore >= 40 ? 'warning' : 'error'
                      }
                      size="small"
                    />
                  </Box>
                  {result.analysis.personaReview.qualityBreakdown && (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Dimension</strong></TableCell>
                            <TableCell align="center"><strong>Score</strong></TableCell>
                            <TableCell><strong>Evidence</strong></TableCell>
                            <TableCell><strong>Improvement</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(result.analysis.personaReview.qualityBreakdown).map(([dimName, dimData]) => {
                            const pct = (dimData.score || 0) / (dimData.maxScore || 1);
                            return (
                              <TableRow key={dimName}>
                                <TableCell>
                                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                    {dimName.replace(/_/g, ' ')}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={`${dimData.score}/${dimData.maxScore}`}
                                    size="small"
                                    color={pct >= 0.67 ? 'success' : pct >= 0.33 ? 'warning' : 'error'}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {dimData.evidence || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {dimData.improvement || '—'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {result.analysis.personaReview.nextIterationFocus && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      <strong>Focus area:</strong> {result.analysis.personaReview.nextIterationFocus}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Overall Assessment (shown if no narrative or as supplement) */}
              {(!result.analysis?.narrative || result.analysis?.structuredExtractionSucceeded) && (
                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {result.analysis?.narrative ? 'Summary' : 'Overall Assessment'}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {result.analysis?.overallAssessment?.summary || 'No summary available'}
                  </Typography>
                  {result.analysis?.overallAssessment?.recommendations && result.analysis.overallAssessment.recommendations.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Recommendations:
                      </Typography>
                      <List dense>
                        {result.analysis.overallAssessment.recommendations.map((rec, idx) => (
                          <ListItem key={idx}>
                            <ListItemText primary={`• ${rec}`} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Box>
              )}

              {/* Keyword Matches (for keyword_search reviews) */}
              {result.reviewType === 'keyword_search' && result.analysis?.keywordMatches && result.analysis.keywordMatches.length > 0 && (
                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Keyword Analysis
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Keyword</strong></TableCell>
                          <TableCell align="center"><strong>Count</strong></TableCell>
                          <TableCell><strong>Summary</strong></TableCell>
                          <TableCell align="center"><strong>Sentiment</strong></TableCell>
                          <TableCell><strong>Contexts</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {result.analysis.keywordMatches.map((match, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <Chip label={match.keyword} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={match.matchCount} size="small" color="primary" />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{match.summary}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={match.sentiment}
                                size="small"
                                color={match.sentiment === 'positive' ? 'success' : match.sentiment === 'negative' ? 'error' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              <Box display="flex" flexWrap="wrap" gap={0.5}>
                                {match.mainContexts && match.mainContexts.map((context, cidx) => (
                                  <Chip key={cidx} label={context} size="small" variant="outlined" />
                                ))}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Safety Flags (for safety_review) */}
              {result.reviewType === 'safety_review' && result.analysis?.safetyFlags && result.analysis.safetyFlags.length > 0 && (
                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Safety Assessment
                  </Typography>
                  <List>
                    {result.analysis.safetyFlags.filter(f => f.detected).map((flag, idx) => (
                      <ListItem key={idx}>
                        <Box display="flex" alignItems="center" gap={2} width="100%">
                          <Chip
                            label={flag.severity}
                            color={getSeverityColor(flag.severity)}
                            size="small"
                          />
                          <Box flex={1}>
                            <Typography variant="subtitle2">{(flag.flagType || 'Unknown').replace(/_/g, ' ')}</Typography>
                            <Typography variant="body2" color="text.secondary">{flag.evidence}</Typography>
                            {flag.recommendedAction && (
                              <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                                <strong>Action:</strong> {flag.recommendedAction}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Findings (generic for all review types) */}
              {result.analysis?.findings && result.analysis.findings.length > 0 && (
                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Detailed Findings
                  </Typography>
                  <List>
                    {result.analysis.findings.map((finding, idx) => (
                      <ListItem key={idx}>
                        <Box>
                          <Typography variant="subtitle2">{finding.finding}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {Array.isArray(finding.evidence) ? finding.evidence.join(' | ') : finding.evidence}
                          </Typography>
                          {finding.requiresAction && finding.actionType && (
                            <Chip
                              label={`Action: ${finding.actionType.replace(/_/g, ' ')}`}
                              color="warning"
                              size="small"
                              sx={{ mt: 1 }}
                            />
                          )}
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Context Snapshot Info */}
              <Box mt={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Review Scope:</strong> {result.contextSnapshot?.messagesIncluded || 0} messages, {result.contextSnapshot?.assessmentsIncluded?.length || 0} assessments, {result.contextSnapshot?.goalsIncluded || 0} goals
                  {result.contextSnapshot?.contextTruncated && ' (context truncated)'}
                </Typography>
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          {selectedJob && selectedJob.status === 'completed' && (
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => {
                setExportTarget(selectedJob.jobId);
                setExportScope('job');
                setExportModalOpen(true);
              }}
            >
              Export All
            </Button>
          )}
          <Button onClick={() => setResultsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Export Modal */}
      <ClientReviewExportModal
        open={exportModalOpen}
        onClose={() => {
          setExportModalOpen(false);
          setExportTarget(null);
        }}
        resultId={exportScope === 'result' ? exportTarget : undefined}
        jobId={exportScope === 'job' ? exportTarget : undefined}
        scope={exportScope}
        title={exportScope === 'result' ? 'Export Client Review' : 'Export All Results'}
        token={token}
      />

      {/* Share Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Share Review</DialogTitle>
        <DialogContent>
          {shareLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Share this review with therapists or supervisors who need visibility.
              </Typography>

              {/* Select users to share with */}
              {eligibleShareUsers.length > 0 ? (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Select users to share with:
                  </Typography>
                  <Autocomplete
                    multiple
                    options={eligibleShareUsers.filter(u =>
                      !currentShares.some(s => s.userId === u._id)
                    )}
                    getOptionLabel={(option) => `${option.name} (${option.role?.replace(/_/g, ' ')})`}
                    value={selectedShareUsers}
                    onChange={(e, value) => setSelectedShareUsers(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search users..."
                        size="small"
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box>
                          <Typography variant="body2">{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.role?.replace(/_/g, ' ')}
                          </Typography>
                        </Box>
                      </li>
                    )}
                  />
                  <Button
                    variant="contained"
                    onClick={handleShare}
                    disabled={selectedShareUsers.length === 0}
                    sx={{ mt: 2 }}
                    fullWidth
                  >
                    Share with {selectedShareUsers.length} user{selectedShareUsers.length !== 1 ? 's' : ''}
                  </Button>
                </Box>
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No eligible users available to share with.
                </Alert>
              )}

              {/* Current shares */}
              {currentShares.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Currently shared with:
                  </Typography>
                  <List dense>
                    {currentShares.map((share) => (
                      <ListItem
                        key={share.userId}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleUnshare(share.userId)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={share.userName}
                          secondary={
                            <>
                              {share.userRole?.replace(/_/g, ' ')}
                              {share.sharedByName && ` • Shared by ${share.sharedByName}`}
                              {share.sharedAt && ` • ${format(new Date(share.sharedAt), 'MMM d, yyyy')}`}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ClientReviewsView;
